import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Button, Card, Grid, Typography } from '@mui/material';
import { Widget404, LoadSkeleton } from '../../../components/widegts';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getServices } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

const StyledTitle = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

const StyledIntro = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box',
});

// ----------------------------------------------------------------------

ServiceCard.propTypes = {
  service: PropTypes.object.isRequired,
  index: PropTypes.number,
};

function ServiceCard({ service, index }) {
  const { slug, title, intro } = service;
  const linkto = `/service/${slug}`;

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ delay: Math.min(index, 5) * 0.08 }}
        sx={{ width: '100%' }}
      >
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2.5, sm: 3 },
            transition: 'transform 0.35s ease, box-shadow 0.35s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 16px 32px ${alpha(theme.palette.grey[900], 0.14)}`,
              },
            },
          }}
        >
          <StyledTitle
            component={RouterLink}
            to={linkto}
            variant="h5"
            sx={{
              color: 'text.primary',
              textAlign: 'center',
              textDecoration: 'none',
              transition: 'color 0.2s ease',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {title}
          </StyledTitle>

          <StyledIntro
            variant="body2"
            sx={{ mt: 2, color: 'text.secondary', textAlign: 'center', lineHeight: 1.75, flexGrow: 1 }}
          >
            {intro}
          </StyledIntro>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to={linkto}
              variant="contained"
              endIcon={<Iconify icon="eva:arrow-forward-outline" />}
            >
              Read more
            </Button>
          </Box>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

MoreServiceWidget.propTypes = {
  currentservice: PropTypes.string,
};

export default function MoreServiceWidget({ currentservice }) {
  const dispatch = useDispatch();
  const { services, error, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  const hasServices = Array.isArray(services) && services.length > 0;

  if (!hasServices && isLoading) {
    return <LoadSkeleton />;
  }

  if (!hasServices && error) {
    return <Widget404 item="Service" />;
  }

  if (!hasServices) {
    return null;
  }

  const moreservices = services.filter((_service) => _service.slug !== currentservice);

  return (
    <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
      {moreservices.map((service, index) => (
        <ServiceCard key={service.slug ?? index} service={service} index={index} />
      ))}
    </Grid>
  );
}
